import React, { useState, useContext } from "react";
import { AppContext } from "../context";
import Navbar from "./Navbar";
import OtpVerification from "./OtpVerification";
import { getLibertyClaimStatus } from "../context/utils";

/**
 * A passed verification is remembered in localStorage, so re-opening the link or
 * a sudden refresh does not demand a fresh SMS. It carries its own expiry and is
 * scoped to the link, so it cannot carry over to another claim.
 *
 * localStorage rather than sessionStorage because sessionStorage dies with the
 * tab: closing the link and opening it again would have asked for a new code.
 * The backend keeps a matching flag for the same hour (see utils/otp.js).
 */
const OTP_TOKEN_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Identity of the link, read from the URL fragment -- the app takes its own token
 * from window.location.hash (see Loading.js), and a refresh keeps the URL, so
 * this is the same string on every load of the same link.
 *
 * Deliberately NOT the context's inspectionId: setInspectionId is called on one
 * branch only, so on most flows it is still null when Liberty renders. Writing
 * under an id and reading back without one (or the reverse) misses the token and
 * asks the user to verify all over again.
 */
const linkKey = () => {
  try {
    const fromHash = (window.location.hash || "").split("#")[1];
    return fromHash ? fromHash.slice(0, 80) : "na";
  } catch (e) {
    return "na";
  }
};

const otpStorageKey = () => `otp_verified_${linkKey()}`;

/** True when a stored pass exists and is still in date. Clears a stale one. */
const readOtpToken = () => {
  try {
    const raw = localStorage.getItem(otpStorageKey());
    if (!raw) return false;
    const { exp } = JSON.parse(raw);
    if (typeof exp === "number" && Date.now() < exp) return true;
    localStorage.removeItem(otpStorageKey());
    return false;
  } catch (e) {
    // Unparseable, or storage blocked in private mode: just verify again.
    return false;
  }
};

const writeOtpToken = () => {
  try {
    localStorage.setItem(
      otpStorageKey(),
      JSON.stringify({ exp: Date.now() + OTP_TOKEN_TTL_MS })
    );
  } catch (e) {
    /* non-fatal: the pass simply will not survive a reload */
  }
};

/**
 * Claim progress stages, in order. The claim status (getLibertyClaimStatus)
 * names one of them; the names are also what the tracker shows.
 */
const STAGES = [
  { code: "A", name: "Claim Registered" },
  { code: "B", name: "Surveyor Appointed" },
  { code: "C", name: "Survey Done" },
  { code: "D", name: "Work Approval" },
  { code: "E", name: "Re-inspection" },
  { code: "F", name: "Pre / Tax Invoice" },
  { code: "G", name: "Delivery Order" },
  { code: "H", name: "Payment" },
];

/** Lowercase, and reduce punctuation to single spaces, for tolerant matching. */
const normalise = (value) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Which stage a claim status names (1-based), or null when it names none.
 *
 * Accepts the stage name ("Assessment"), a stage code ("C"), a number (3), or
 * something close to a name ("survey complete"). Partial matching needs four
 * characters, so a stray "re" cannot land on "Registered".
 *
 * Returns null for an empty or unrecognised status; the caller decides what to
 * show then (see DEFAULT_STAGE).
 */
const stageFromStatus = (raw) => {
  if (raw === undefined || raw === null || String(raw).trim() === "")
    return null;

  const asNumber = Number(raw);
  if (Number.isInteger(asNumber) && asNumber >= 1 && asNumber <= STAGES.length)
    return asNumber;

  const text = normalise(raw);
  if (!text) return null;

  const byCode = STAGES.findIndex((s) => s.code.toLowerCase() === text);
  if (byCode !== -1) return byCode + 1;

  const byName = STAGES.findIndex((s) => normalise(s.name) === text);
  if (byName !== -1) return byName + 1;

  if (text.length < 4) return null;
  const byPartial = STAGES.findIndex((s) => {
    const name = normalise(s.name);
    return name.includes(text) || text.includes(name);
  });
  return byPartial === -1 ? null : byPartial + 1;
};

/**
 * Where a finished stage's date may be carried, when the client sends any.
 *
 * inputMetaData is free-form, so the tracker reads a container of dates under
 * any of these names and looks inside it by stage code ("C"), by 1-based
 * position, or by stage name -- an array works too. Nothing here is required:
 * a stage with no date just reads "Completed".
 */
const STAGE_DATE_KEYS = ["stage_dates", "stageDates", "claim_stage_dates"];

const stageDate = (meta, index) => {
  const stage = STAGES[index];
  if (!meta || !stage) return "";
  for (const key of STAGE_DATE_KEYS) {
    const held = meta[key];
    if (!held) continue;
    const candidates = Array.isArray(held)
      ? [held[index]]
      : [held[stage.code], held[index + 1], held[stage.name]];
    const hit = candidates.find(
      (v) => v !== undefined && v !== null && v !== ""
    );
    if (hit) return String(hit);
  }
  // Stage one is the claim being registered, which is the intimation date --
  // already on this page, so the first step can be dated without new payload.
  if (index === 0 && meta.intimation_date) return String(meta.intimation_date);
  return "";
};

/**
 * The current stage when the claim status is empty or unrecognised.
 *
 * A link only exists once the claim is registered and a surveyor has been
 * appointed to it -- that surveyor is who sends it -- so with nothing else to
 * go on, Registered and Surveyor show as completed and Assessment in progress.
 */
const DEFAULT_STAGE = 3;

/** A label / value pair in one of the detail grids. */
const Field = ({ label, value }) => (
  <div className="claim-field">
    <div className="claim-field-label">{label}</div>
    <div className="claim-field-value">{value || "--"}</div>
  </div>
);

const Liberty = () => {
  const {
    skipStencil,
    setSkipStencil,
    alignStencil,
    setalignStencil,
    capturedStencil,
    setcapturedStencil,
    stepCovered,
    setStepCovered,
    setScreen,
    currentBlock,
    condition,
    setCondition,
    config,
    token,
    mandatory,
    setMandatory,
    inspectionId,
    setStopPing,
    setCurrentPage,
    stopPing,
    restrictUserIfallNotGreen,
    setRestrictUserIfallNotGreen,
    clientId,
    caseId,
    isIOS,
    inputMetaData,
  } = useContext(AppContext);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Same primary colour as every other button in the app; the navy fallback is
  // the shade this page was designed in.
  const accent =
    (config && config["colors"] && config["colors"]["btn"]) || "#1c1550";

  // The claim status is the last stage the claim has completed, so the current
  // stage is the one after it ("Assessment" -> Approval in progress). Past the
  // last stage everything reads completed. Empty: DEFAULT_STAGE.
  const completedStage = stageFromStatus(getLibertyClaimStatus());
  const stage =
    completedStage === null ? DEFAULT_STAGE : completedStage + 1;

  // Verification is a step on the way OUT of this screen, not a gate in front of
  // it: the claim details are shown first, and the number is verified when the
  // user presses Proceed. A pass from the last hour is honoured, so a returning
  // user goes straight through -- the key comes from the URL, known on the very
  // first render, and expiry is only consulted here, on arrival.
  const [phoneVerified, setPhoneVerified] = useState(() => readOtpToken());
  const [showOtp, setShowOtp] = useState(false);

  const goToNextScreen = () => {
    if (config["email"]) {
      setScreen("email");
    } else {
      setScreen("instructions");
    }
  };

  const handleProceed = () => {
    if (!acceptedTerms) return;
    // Already verified within the hour: no second SMS, straight on.
    if (phoneVerified) {
      goToNextScreen();
      return;
    }
    setShowOtp(true);
  };

  const handleVerified = () => {
    writeOtpToken();
    setPhoneVerified(true);
    // The user pressed Proceed to get here, so carry on rather than dropping them
    // back on the claim page to press it again.
    goToNextScreen();
  };

  if (showOtp) {
    return (
      <OtpVerification
        onVerified={handleVerified}
        onBack={() => setShowOtp(false)}
      />
    );
  }

  // One variable drives the button, the progress bar and the links, so a client's
  // primary colour styles the whole page.
  return (
    <div className="claim-page" style={{ "--claim-accent": accent }}>
      <Navbar />

      <div className="claim-card">
        <div className="claim-scroll">
          {/* Every stage is listed, with the live one marked: the one after
              the claim status, or DEFAULT_STAGE when it is empty. Four to a
              row, so all eight are on the page at once without scrolling. */}
          <div className="claim-stage-tracker">
            <div className="claim-stage-head">
              <span className="claim-stage-label">Claim Status</span>
              <span className="claim-stage-count">
                {`Stage ${Math.min(stage, STAGES.length)} of ${STAGES.length}`}
              </span>
            </div>
            <div className="claim-steps">
              {STAGES.map((s, i) => {
                const n = i + 1;
                const state =
                  n < stage ? "done" : n === stage ? "current" : "todo";
                const date = stageDate(inputMetaData, i);
                return (
                  <div
                    key={s.code}
                    className={`claim-step claim-step-${state}`}
                  >
                    <span className="claim-step-dot">
                      {state === "done" ? (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : (
                        n
                      )}
                    </span>
                    <span className="claim-step-name">{s.name}</span>
                    <span className="claim-step-sub">
                      {state === "done"
                        ? date || "Completed"
                        : state === "current"
                          ? "In Progress"
                          : "Pending"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="claim-section">
            <h2 className="claim-section-title">Claim Details</h2>
            <div className="claim-field-grid">
              <Field label="Claim Number:" value={inputMetaData?.claim_number} />
              <Field
                label="Intimation Date:"
                value={inputMetaData?.intimation_date}
              />
              <Field
                label="Policy Number:"
                value={inputMetaData?.policy_number}
              />
            </div>
          </div>

          <div className="claim-section">
            <h2 className="claim-section-title">Vehicle</h2>
            <div className="claim-field-grid">
              <Field
                label="Manufacturer:"
                value={inputMetaData?.manufacturer_name}
              />
              <Field label="Model:" value={inputMetaData?.model} />
              <Field label="Variant:" value={inputMetaData?.variant} />
              <Field
                label="Registration No:"
                value={inputMetaData?.vehicle_number}
              />
              <Field
                label="Year of Registration:"
                value={inputMetaData?.year_of_manufacture}
              />
            </div>
          </div>

          <div className="claim-section claim-section-last">
            <h2 className="claim-section-title">Insured</h2>
            <div className="claim-field-value">
              {inputMetaData?.insured_name || "--"}
            </div>
          </div>

        </div>

        {/* Pinned to the bottom of the card, outside the scroll area, so the
            consent and the action stay reachable however long the details are. */}
        <div className="claim-footer">
          <div className="claim-consent">
            <input
              type="checkbox"
              id="termsCheckbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <label htmlFor="termsCheckbox">
              I have read and agree the Terms of Service and Privacy Policy.
            </label>
          </div>

          <div className="claim-cta-wrap">
            <button
              className={`claim-primary-btn ${
                acceptedTerms ? "claim-enabled" : ""
              }`}
              onClick={handleProceed}
              disabled={!acceptedTerms}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Liberty;
